import PropTypes from 'prop-types';

import {Flex, Box, Grid, GridItem} from '@salesforce/retail-react-app/app/components/shared/ui';

import {Component} from '@amblique/king-living/app/components/contentful/component';
import {sectionPanelAppearanceClass} from '@amblique/king-living/app/contentful/models/section-panel/section-panel-appearance-class';

/**
 * Editorial Feature Block (CF-020)
 *
 * @typedef {import('@chakra-ui/styled-system').SystemStyleObject} SystemStyleObject
 * @typedef {"root"} SectionPanelParts
 * @typedef {Partial<Record<SectionPanelParts, SystemStyleObject>>} SectionPanelStyles
 *
 * @param {Object} props - Component props
 * @param {Contentful.SectionPanel} props.component - SectionPanel component data
 * @param {boolean} [props.isComplete] - Whether component has all fields
 * @param {boolean} [props.isFetching] - Whether additional data is being fetched
 * @param {SectionPanelStyles} [props.styles] - Whether additional data is being fetched
 * @returns {React.ReactElement}
 */
export const EditorialFeatureBlockCF020 = ({
  component,
  isComplete = true,
  isFetching = false,
  styles,
  className,
  ...props
}) => {
  const {contentCollection, appearance} = component || {};

  if (!contentCollection?.items) {
    return null;
  }

  const validItems = contentCollection.items.filter(
    (item) => item != null && typeof item === 'object' && item.__typename && item.sys?.id
  );

  if (validItems.length === 0) {
    return null;
  }

  const appearanceModifier = sectionPanelAppearanceClass(appearance);

  return (
    <>
      {appearance === 'Editorial Feature Block - (CF-020) [1]' ? (
        <Box
          {...styles.container}
          {...props}
          className={
            [
              'editorial-feature-block-cf020',
              'editorial-feature-block-cf020--layout-1',
              appearanceModifier,
              styles.container?.className,
              className,
            ]
              .filter(Boolean)
              .join(' ')
          }
        >
          {validItems.map((item, index) => (
            <Component key={item.sys.id} component={item} isComplete={isComplete} isFetching={isFetching} styles={styles} />
          ))}
        </Box>
      ) : appearance === 'Editorial Feature Block - (CF-020) [2]' ? (
        <Flex
          {...styles.container}
          {...props}
          className={
            [
              'editorial-feature-block-cf020',
              'editorial-feature-block-cf020--layout-2',
              appearanceModifier,
              styles.container?.className,
              className,
            ]
              .filter(Boolean)
              .join(' ')
          }
        >
          {validItems.map((item, index) => {
            const region = index === 0 ? styles.leftRegion : styles.rightRegion;
            const side = index === 0 ? 'left' : 'right';

            return (
              <Box
                key={item.sys.id}
                {...region}
                className={
                  [
                    'editorial-feature-block-cf020__column',
                    `editorial-feature-block-cf020__column--${side}`,
                    appearanceModifier,
                    region?.className,
                  ]
                    .filter(Boolean)
                    .join(' ')
                }
              >
                <Component component={item} isComplete={isComplete} isFetching={isFetching} styles={styles} />
              </Box>
            );
          })}
        </Flex>
      ) : appearance === 'Editorial Feature Block - (CF-020) [3]' ? (
        <Grid
          {...styles.container}
          {...props}
          className={
            [
              'editorial-feature-block-cf020',
              'editorial-feature-block-cf020--layout-3',
              appearanceModifier,
              styles.container?.className,
              className,
            ]
              .filter(Boolean)
              .join(' ')
          }
        >
          {validItems.map((item, index) => (
            <GridItem
              key={item.sys.id}
              className={
                [
                  'editorial-feature-block-cf020__cell',
                  `editorial-feature-block-cf020__cell--${index}`,
                  appearanceModifier,
                ]
                  .filter(Boolean)
                  .join(' ')
              }
            >
              <Component component={item} isComplete={isComplete} isFetching={isFetching} styles={styles} />
            </GridItem>
          ))}
        </Grid>
      ) : null}
    </>);
};

EditorialFeatureBlockCF020.displayName = 'EditorialFeatureBlockCF020';
EditorialFeatureBlockCF020.propTypes = {
  component: PropTypes.object.isRequired,
  isComplete: PropTypes.bool,
  isFetching: PropTypes.bool,
  styles: PropTypes.object.isRequired,
  className: PropTypes.string,
};
